// ---------------------------------------------------------------------------
// Structured filter wire converters
// ---------------------------------------------------------------------------
// The special-cased filters (workflow_node_mappings, entity) persist a rich
// value with labels (so editing a saved widget rehydrates with names) but the
// backend /query and /preview endpoints want a bare wire shape. These
// converters run on the READ paths only — never on save (save persists the rich
// value verbatim). See AN-7625 design doc.
//
// Backend wire shapes (locked in and covered by structured-filters.test.ts):
//  - workflow_node_mappings → Array<{workflow_id, node_id[]}>, "show all" → []
//    (mirrors discover-job-search/view-container.tsx)
//  - entity filter → {and: [...]} predicate tree (delegates to transformEntities
//    in src/utils/analytics-genesis.ts)

import {transformEntities} from 'src/utils/analytics-genesis';
import {
  WORKFLOW_NODE_MAPPING_KEY,
  ENTITY_FILTER_KEY,
} from 'src/components/analytics-genesis/byod/constants';
import type {WorkflowNodeMappingValue, EntityFilterValue} from 'src/types/byod';

// Per-mapping key names match the widget definition's `hierarchical_bindings`
// (e.g. total_unsubscribes binds keys `workflow_id` + `node_id`). `node_id`
// carries the selected node ids as an array for an IN-style binding (one clause
// per workflow); locked in and covered by structured-filters.test.ts. If the
// backend later requires a scalar-per-node shape, change here + the test.
export type WorkflowNodeMappingWire = Array<{
  workflow_id: string;
  node_id: string[];
}>;

/** Type guard: is this the rich workflow value (vs a plain string[])? */
export function isWorkflowNodeMappingValue(
  v: unknown,
): v is WorkflowNodeMappingValue {
  return (
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    Array.isArray((v as WorkflowNodeMappingValue).workflows)
  );
}

/** Type guard: is this the rich entity value (array of row objects)? */
export function isEntityFilterValue(v: unknown): v is EntityFilterValue {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    typeof v[0] === 'object' &&
    v[0] !== null
  );
}

/**
 * workflow_node_mappings → bare wire shape. `show_all` (or an empty value)
 * collapses to `[]`. Local row ids are ignored. One clause per selected
 * workflow; a workflow with no nodes selected sends `node_id: []`.
 */
export function workflowNodeMappingsToWire(
  value: WorkflowNodeMappingValue | undefined | null,
): WorkflowNodeMappingWire {
  if (!value || value.show_all || !Array.isArray(value.workflows)) {
    return [];
  }
  return value.workflows.flatMap((row) =>
    (row.workflow_ids ?? []).map((wf) => ({
      workflow_id: String(wf.key),
      node_id: (row.node_ids ?? []).map((n) => String(n.key)),
    })),
  );
}

/**
 * Entity rows → `{and: [...]}` predicate tree. Delegates to the shared
 * `transformEntities`, which drops rows missing entity type / attribute / value
 * (so an empty or partial filter yields `{and: []}`) and maps each value:
 *   array → `in`; {min,max} → `and(lte, gte)`; scalar → `eq`.
 */
export function entityFilterToWire(
  value: EntityFilterValue | undefined | null,
): ReturnType<typeof transformEntities> {
  return transformEntities(value ?? []);
}

/**
 * Convert any structured filter values in an active-filters map to their wire
 * shape, leaving plain `string[]` (and injected date-range) values untouched.
 * Call this when building the /query and /preview request bodies — NOT on save.
 * The filter KEY is preserved; only the value is transformed.
 */
export function convertStructuredFiltersForWire(
  filters: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (key === WORKFLOW_NODE_MAPPING_KEY) {
      out[key] = workflowNodeMappingsToWire(value as WorkflowNodeMappingValue);
    } else if (key === ENTITY_FILTER_KEY) {
      out[key] = entityFilterToWire(value as EntityFilterValue);
    } else {
      out[key] = value;
    }
  }
  return out;
}
